/**
 * Analytics utilities - integrated with PostHog
 * 
 * This module provides a unified interface for analytics tracking.
 * Events are sent to PostHog for behavioral analytics.
 */

import { analytics as posthog } from '$lib/analytics/posthog';

export type AnalyticsEvent =
  | 'preview_impression'
  | 'cta_full_analysis_click'
  | 'analysis_start_success'
  | 'analysis_start_error'
  | 'error_boundary_triggered'
  | 'error_boundary_retry_clicked'
  | 'paywall_view'
  | 'cta_click'
  | 'studio_home_view'
  | 'studio_module_click'
  | 'studio_module_view'
  | 'studio_breakdown_view'
  | 'studio_schedule_view'
  | 'studio_report_generated'
  | 'quilty_rules_tab_viewed'
  | 'quilty_rules_category_expanded'
  | 'preview_locked_section_viewed'
  | 'preview_unlock_clicked'
  | 'preview_score_teaser_viewed'
  | 'preview_score_hook_shown'
  | 'preview_discovery_cta_click'
  | 'preview_fixes_cta_click'
  | 'preview_scroll_depth'
  | 'preview_report_contents_link_clicked'
  | 'report_contents_row_clicked'
  | 'preview_upgrade_checkout_opened'
  | 'preview_poster_impression'
  | 'preview_poster_cta_clicked'
  | 'preview_poster_lightbox_opened'
  | 'dn_upsell_cta_click'
  | 'dn_upsell_leaderboard_click'
  | 'dn_upsell_manage_click'
  | 'analysis_promo_impression'
  | 'analysis_promo_cta_click'
  | 'preview_labs_upsell_impression'
  | 'preview_labs_upsell_cta'
  | 'preview_poster_thumb_impression'
  | 'preview_poster_thumb_open'
  | 'preview_poster_thumb_cta'
  | 'funnel_pageview'
  | 'funnel_cta_click'
  | 'funnel_upload_started'
  | 'funnel_upload_accepted'
  | 'funnel_preview_completed'
  | 'funnel_checkout_opened'
  | 'funnel_purchase_completed'
  | 'incentive_import_started'
  | 'incentive_import_completed'
  | 'incentive_profile_reviewed'
  | 'incentive_shortlist_add'
  | 'incentive_shortlist_remove'
  | 'incentive_official_source_opened';

export interface AnalyticsPayload {
  screenplay_id?: number;
  user_id?: number;
  timestamp: number;
  [key: string]: any;
}

/**
 * Track analytics event via PostHog
 */
export function trackEvent(
  event: AnalyticsEvent,
  payload: Omit<AnalyticsPayload, 'timestamp'> = {}
) {
  const fullPayload: AnalyticsPayload = {
    ...payload,
    timestamp: Date.now(),
  };

  // Send to PostHog
  posthog.track(event, fullPayload);
}

/**
 * Track preview impression (when user views screenplay detail page)
 */
export function trackPreviewImpression(screenplayId: number, userId?: number) {
  trackEvent('preview_impression', { screenplay_id: screenplayId, user_id: userId });
}

/**
 * Track CTA click for full analysis
 */
export function trackFullAnalysisCTA(screenplayId: number, userId?: number) {
  trackEvent('cta_full_analysis_click', { screenplay_id: screenplayId, user_id: userId });
}

/**
 * Track successful analysis start
 */
export function trackAnalysisStartSuccess(
  screenplayId: number,
  analysisId: number,
  userId?: number
) {
  trackEvent('analysis_start_success', {
    screenplay_id: screenplayId,
    analysis_id: analysisId,
    user_id: userId,
  });
}

/**
 * Track failed analysis start
 */
export function trackAnalysisStartError(screenplayId: number, error: string, userId?: number) {
  trackEvent('analysis_start_error', {
    screenplay_id: screenplayId,
    error,
    user_id: userId,
  });
}

/**
 * Track paywall view (when user sees paywall/upgrade prompt)
 */
export function trackPaywallView(screenplayId: number, userId?: number) {
  trackEvent('paywall_view', { screenplay_id: screenplayId, user_id: userId });
}

/**
 * Track CTA click event (generic CTA tracking)
 */
export function trackCTAClickEvent(screenplayId: number, ctaType: string, userId?: number) {
  trackEvent('cta_click', {
    screenplay_id: screenplayId,
    cta_type: ctaType,
    user_id: userId,
  });
}
